/* global L */
declare global {
    interface Window {
        L?: any
    }
}

let leafletPromise: Promise<any> | null = null

const LEAFLET_VERSION = '1.9.4'
const LEAFLET_JS = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.js`
const LEAFLET_CSS = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.css`

const PHOTON_BASE = 'https://photon.komoot.io/api'
const PHOTON_REVERSE = 'https://photon.komoot.io/reverse'

export function loadLeaflet(): Promise<any> {
    if (typeof window === 'undefined') {
        return Promise.reject(new Error('Leaflet requires a browser environment'))
    }
    if (window.L) return Promise.resolve(window.L)
    if (leafletPromise) return leafletPromise

    leafletPromise = new Promise((resolve, reject) => {
        if (!document.querySelector('link[data-bardy-leaflet]')) {
            const css = document.createElement('link')
            css.rel = 'stylesheet'
            css.href = LEAFLET_CSS
            css.dataset.bardyLeaflet = 'true'
            document.head.appendChild(css)
        }
        const existing = document.querySelector<HTMLScriptElement>('script[data-bardy-leaflet]')
        if (existing) {
            existing.addEventListener('load', () => resolve(window.L))
            existing.addEventListener('error', () => reject(new Error('Failed to load Leaflet')))
            return
        }
        const script = document.createElement('script')
        script.src = LEAFLET_JS
        script.async = true
        script.defer = true
        script.dataset.bardyLeaflet = 'true'
        script.onload = () => {
            if (window.L) resolve(window.L)
            else reject(new Error('Leaflet loaded but global L missing'))
        }
        script.onerror = () => reject(new Error('Failed to load Leaflet'))
        document.head.appendChild(script)
    })

    return leafletPromise
}

export interface ParsedAddress {
    street: string
    number: string
    neighborhood: string
    city: string
    state: string
    zipCode: string
    country: string
    formatted: string
    lat: number | null
    lng: number | null
}

interface PhotonFeature {
    type: 'Feature'
    geometry: { type: 'Point'; coordinates: [number, number] }
    properties: {
        osm_id?: number
        osm_type?: string
        country?: string
        countrycode?: string
        state?: string
        county?: string
        city?: string
        district?: string
        locality?: string
        postcode?: string
        name?: string
        street?: string
        housenumber?: string
        type?: string
        osm_key?: string
        osm_value?: string
    }
}

export interface PhotonSuggestion {
    main: string
    secondary: string
    feature: PhotonFeature
}

function isLikelyAddress(feature: PhotonFeature): boolean {
    const t = feature.properties.osm_value
    if (!t) return true
    // Filter out POIs unrelated to addresses
    const accepted = ['residential', 'house', 'apartment', 'street', 'avenue', 'square',
        'address', 'place', 'neighbourhood', 'suburb', 'city', 'town', 'village']
    return accepted.includes(t) || Boolean(feature.properties.street) || Boolean(feature.properties.housenumber) || Boolean(feature.properties.postcode)
}

export async function photonSearch(query: string, opts: { lang?: string; limit?: number; lat?: number; lon?: number } = {}): Promise<PhotonSuggestion[]> {
    if (!query || query.trim().length < 3) return []
    const params = new URLSearchParams()
    params.set('q', query)
    // Photon só suporta default, de, en, fr — 'pt' é rejeitado com 400. 'default' preserva os
    // nomes originais (em português, no caso de endereços brasileiros no OSM).
    params.set('lang', opts.lang || 'default')
    params.set('limit', String(opts.limit || 8))
    if (opts.lat !== undefined && opts.lon !== undefined) {
        params.set('lat', String(opts.lat))
        params.set('lon', String(opts.lon))
    }
    const res = await fetch(`${PHOTON_BASE}?${params.toString()}`)
    if (!res.ok) throw new Error(`Photon search failed: ${res.status}`)
    const data = await res.json() as { features: PhotonFeature[] }
    return (data.features || [])
        .filter(f => f.geometry?.type === 'Point')
        .filter(isLikelyAddress)
        .map(toSuggestion)
}

export async function photonReverse(lat: number, lon: number, lang = 'default'): Promise<PhotonSuggestion | null> {
    const params = new URLSearchParams()
    params.set('lat', String(lat))
    params.set('lon', String(lon))
    params.set('lang', lang)
    const res = await fetch(`${PHOTON_REVERSE}?${params.toString()}`)
    if (!res.ok) return null
    const data = await res.json() as { features: PhotonFeature[] }
    const f = data.features?.[0]
    if (!f) return null
    return toSuggestion(f)
}

function toSuggestion(feature: PhotonFeature): PhotonSuggestion {
    const p = feature.properties
    const streetPart = [p.street, p.housenumber].filter(Boolean).join(', ')
    const main = streetPart || p.name || p.locality || p.district || p.city || 'Local'
    const secondaryParts = [p.district || p.locality, p.city, p.state, p.country].filter(Boolean)
    const secondary = secondaryParts.join(' · ')
    return { main, secondary, feature }
}

export function parsePhotonAddress(feature: PhotonFeature): ParsedAddress {
    const p = feature.properties
    const [lng, lat] = feature.geometry.coordinates
    const street = p.street || p.name || ''
    const number = p.housenumber || ''
    const neighborhood = p.district || p.locality || p.county || ''
    const city = p.city || p.locality || ''
    const state = p.state || ''
    const zipCode = p.postcode || ''
    const country = p.countrycode?.toUpperCase() || p.country || ''
    const formatted = [
        [street, number].filter(Boolean).join(', '),
        neighborhood,
        [city, state].filter(Boolean).join('/'),
        zipCode
    ].filter(Boolean).join(' - ')
    return {
        street,
        number,
        neighborhood,
        city,
        state,
        zipCode,
        country,
        formatted,
        lat,
        lng
    }
}
